import { useEffect, useId, useRef } from 'react'

/**
 * 팝업 오버레이 접근성 — ui-core Modal(D-09 §15.6, packages/ui-core/src/components/Modal.tsx)과
 * 동일한 포커스트랩·Esc 닫기·열릴 때 포커스 이동/닫힐 때 이전 포커스 복원 동작을 스케줄러의
 * 커스텀(대형/탭형) 팝업에도 적용한다. ui-core Modal은 `.modal-box`가 max-width 420px로
 * 고정돼 있어(packages/styles/src/components.css:115) 간트차트 자재등록·시공반장 배정처럼
 * 폭이 넓거나 탭이 있는 팝업엔 그대로 재사용할 수 없으므로, 동일한 접근성 로직만 훅으로
 * 옮겨 각 팝업의 콘텐츠 박스(오버레이 바로 안쪽, 흰 배경 div)에 씌운다.
 *
 * `open`을 명시적으로 받는다 — SigongPopup처럼 부모가 조건부 렌더링(`{cond && <Popup/>}`)으로
 * 마운트할 때는 항상 true를 넘기면 되고, GanttView/CalendarScheduleView처럼
 * 한 컴포넌트 안에 여러 팝업이 상태 플래그로만 토글되는 경우엔 그 플래그를 그대로 넘기면
 * 매번 열릴 때마다 포커스 이동·트랩이 다시 걸린다.
 */
export function useModalA11y(open: boolean, onClose: () => void) {
  const boxRef = useRef<HTMLDivElement>(null)
  const titleId = useId()

  useEffect(() => {
    if (!open) return
    const previouslyFocused = document.activeElement as HTMLElement | null
    const box = boxRef.current
    const selector =
      'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])'
    const getItems = () =>
      box ? Array.from(box.querySelectorAll<HTMLElement>(selector)) : []

    ;(getItems()[0] ?? box)?.focus()

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.stopPropagation(); onClose(); return }
      if (e.key !== 'Tab') return
      const items = getItems()
      if (items.length === 0) { e.preventDefault(); box?.focus(); return }
      const first = items[0]
      const last = items[items.length - 1]
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus() }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus() }
    }

    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      previouslyFocused?.focus?.()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  return { boxRef, titleId }
}
